/* Tell the shopper the truth about the button they are pressing.

   The product page renders two buy buttons. The real one lives in the form and
   the theme disables it when the selected variant cannot be bought. The one
   the shopper actually sees and presses — .dummy-add-to-cart, inside the
   description block — is rendered once by Liquid from `product.available`,
   which is true if ANY variant of the product has stock, and is never updated
   again. So on a sold-out size it still reads ADD TO CART, forwards a click to
   a disabled button, and nothing happens: it reads as a broken site rather
   than as a sold-out size.

   An earlier version of this file mirrored the real button's state onto the
   decoy. That was the wrong idea — it made this file depend on theme internals
   that change between variant renders, and in at least one state the buttons
   vanished entirely, which is worse than the bug it was fixing.

   This version depends on nothing but the variant JSON the theme already
   inlines for its own use, the same source the buckle drawer reads. Three
   states, and the button is never hidden in any of them:

     ok           the variant exists and can be bought — leave everything alone
     sold out     the variant exists but has no stock
     unavailable  this combination of options was never made

   The last one matters here: Croc99 comes in 16mm and 23mm only in Sharp
   Tuxedo with Red Lining, so most colours have two sizes that are not "out of
   stock" but simply do not exist. Saying "sold out" there would be a lie.

   24 Sep 2026 — DEFERRING TO THE GUIDED ATC FLOW

   Whenever assets/wc-guided-atc.js is on the page, Shopify's own "selected
   variant" can read as null/unavailable simply because the shopper hasn't
   finished choosing yet (Lug Width, Buckle/Clasp, etc. still open) — that is
   not the same thing as the product being unbuyable. Previously this file
   painted SOLD OUT/UNAVAILABLE the instant that happened, regardless of
   guided sequencing, while wc-guided-atc.js kept repainting its own pending
   ADD TO CART state on its own independent timers. Two scripts unconditionally
   writing the same button from two different sources of truth, on two
   uncoordinated clocks, is exactly what produced the flicker — and because an
   unresolved option is the NORMAL state for most of a shopper's time on the
   page, the fight ran continuously until every option was chosen, not just
   in a brief transition.

   The fix is to defer outright: if wc-guided-atc.js is present and reports a
   step is still open, this file does nothing at all — no text, no class, no
   style — and leaves the button to the guided flow entirely, exactly as this
   file's normal behaviour already promises ("leave everything alone") once
   sequencing is out of the way. window.__wcGuidedIncomplete() is computed
   fresh by wc-guided-atc.js on every call, from the live DOM, so there is
   nothing stale being read here — only a live answer checked at this file's
   own tick, the same way wc-guided-atc.js now checks this file's availability
   truth independently rather than through a cached class (see that file's
   own 24 Sep 2026 note). */

(function () {
  'use strict';

  if (window.__wcAtcState) return;
  window.__wcAtcState = true;

  var COPY = {
    soldout: 'This size and colour is sold out. Please choose another.',
    unavailable: 'This combination isn’t available. Please choose another size or colour.'
  };

  var LABEL = { ok: null, soldout: 'SOLD OUT', unavailable: 'UNAVAILABLE' };

  function style() {
    if (document.getElementById('wc-atc-state-css')) return;
    var s = document.createElement('style');
    s.id = 'wc-atc-state-css';
    s.textContent =
      /* Nothing this file does may ever remove the buy button from the page. */
      '.dummy-buttons-wrapper{display:flex !important;}' +
      '.dummy-add-to-cart{display:flex !important;}' +
      '.dummy-add-to-cart.wc-unbuyable,.dummy-buy-now.wc-unbuyable{' +
        'background-color:#ffffff !important;color:#9a9a9a !important;' +
        'border:1px solid #d8d8d8 !important;cursor:not-allowed;opacity:1 !important;}' +
      '.wc-atc-note{margin:-20px 0 26px;font-size:1.3rem;line-height:1.5;' +
        'color:#b3261e;letter-spacing:.02em;}' +
      '.wc-atc-note[hidden]{display:none !important;}';
    document.head.appendChild(s);
  }

  /* The theme inlines the selected variant for its own price and button
     rendering. When the chosen combination does not exist it inlines null,
     which is exactly the signal needed to tell "never made" from "sold out". */
  function selectedVariant() {
    var el = document.querySelector('variant-selects script[data-selected-variant]');
    if (!el) return undefined;              // no picker on this page
    try { return JSON.parse(el.textContent); } catch (e) { return undefined; }
  }

  /* A required choice (Lug Width, Buckle/Clasp, ...) still being open is not
     evidence the product can't be bought — it just means Shopify has nothing
     to match yet. Whenever wc-guided-atc.js is on the page, defer to it
     completely until it reports every step settled, so the two scripts are
     never painting the same button from two different, uncoordinated
     answers at once. Absent that file entirely (a product with no guided
     flow), this is simply false and changes nothing. */
  function guidedIncomplete() {
    return typeof window.__wcGuidedIncomplete === 'function' && window.__wcGuidedIncomplete();
  }

  function stateNow() {
    var v = selectedVariant();
    if (v === undefined) return 'ok';       // nothing to judge — stay out of the way
    if (v === null) return 'unavailable';
    return v.available === false ? 'soldout' : 'ok';
  }

  function note(wrapper) {
    var el = wrapper.parentNode.querySelector('.wc-atc-note');
    if (!el) {
      el = document.createElement('p');
      el.className = 'wc-atc-note';
      el.setAttribute('role', 'status');
      el.hidden = true;
      wrapper.parentNode.insertBefore(el, wrapper.nextSibling);
    }
    return el;
  }

  var state = 'ok';

  function paint() {
    var wrapper = document.querySelector('.dummy-buttons-wrapper');
    var atc = document.querySelector('.dummy-add-to-cart');
    if (!wrapper || !atc) return;

    /* Defer entirely while guided sequencing is still open — see the file
       header. Nothing here is touched: no text, no class, no style. The
       guided flow's own pending state is left as the only thing painting
       the button until it reports every step settled. */
    if (guidedIncomplete()) return;

    state = stateNow();
    var bad = state !== 'ok';

    if (!atc.getAttribute('data-wc-label')) {
      atc.setAttribute('data-wc-label', (atc.textContent || 'ADD TO CART').trim());
    }

    var text = bad ? LABEL[state] : atc.getAttribute('data-wc-label');
    if ((atc.textContent || '').trim() !== text) atc.textContent = text;

    atc.classList.toggle('wc-unbuyable', bad);
    atc.setAttribute('aria-disabled', bad ? 'true' : 'false');
    /* Deliberately NOT the disabled attribute: a disabled button swallows the
       click, and the click is how the message gets shown to someone who taps
       it anyway. */
    atc.disabled = false;

    var buy = document.querySelector('.dummy-buy-now');
    if (buy) {
      buy.classList.toggle('wc-unbuyable', bad);
      buy.setAttribute('aria-disabled', bad ? 'true' : 'false');
    }

    /* The message only appears once they have actually tried. */
    if (!bad) note(wrapper).hidden = true;
  }

  function explain() {
    var wrapper = document.querySelector('.dummy-buttons-wrapper');
    if (!wrapper) return;
    var el = note(wrapper);
    el.textContent = COPY[state] || COPY.unavailable;
    el.hidden = false;
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /* Capture phase, so this runs before the theme's own handler forwards the
     click to a button that cannot accept it. */
  document.addEventListener('click', function (e) {
    var hit = e.target.closest && e.target.closest('.dummy-add-to-cart, .dummy-buy-now');
    if (!hit) return;
    if (state === 'ok') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    explain();
  }, true);

  function watch() {
    var host = document.querySelector('product-info')
      || document.querySelector('.product__info-wrapper');
    if (!host || !window.MutationObserver) return;

    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; paint(); }, 60);
    }).observe(host, { childList: true, subtree: true });
  }

  function boot() {
    if (!document.querySelector('.dummy-add-to-cart')) return;   // not a product page
    style();
    paint();
    watch();
    document.addEventListener('change', function () { setTimeout(paint, 80); }, true);
    /* The guided flow's own settle events (native option change, the buckle
       drawer's confirm, keepers) are exactly the moments this file's
       deferral can flip to false — repaint right away rather than waiting
       on this file's own slower triggers to catch up. */
    document.addEventListener('wc-buckle:change', paint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
